/**
 * Chart documentation shown behind the info icon on the Users dashboard.
 *
 * Keys match the chart link property names in `MopaUsersComponent`.
 */
export const MopaUsersChartDocs = {
    dau: /* HTML */ `<p>
            Daily Active Users - the number of distinct players active on a
            given date.
        </p>
        <p>
            <strong>What's counted:</strong> distinct <code>player_id</code> per
            day (<code>COUNT(DISTINCT player_id)</code>). A player is active on
            any day they have a row in the activity table, regardless of session
            length; each player counts once per day no matter how many sessions
            they play.
        </p>
        <p>Source: <code>bi_viz.dashboard_users_activity</code>.</p>`,

    daily_new_users: /* HTML */ `<p>
            Number of players who opened the app for the very first time on a
            given date.
        </p>
        <p>
            <strong>What's counted:</strong> distinct players whose first-open
            date equals that date - day 0 of their lifetime (<code
                >COUNT(DISTINCT d0_player_id)</code
            >). A player appears in this series on exactly one date, ever.
        </p>
        <p>Source: <code>bi_viz.dashboard_users_activity</code>.</p>`,

    m_avg_dau: /* HTML */ `<p>
            Average number of daily active users on a typical day within a
            calendar month.
        </p>
        <pre>
  sum of each day's DAU across the month       (numerator)
  ───────────────────────────────────────
  number of distinct days with data           (denominator)</pre
        >
        <p>
            The <strong>denominator</strong> is days-with-data, <em>not</em> the
            calendar day count - so an in-progress or partially-loaded month is
            not understated (dividing 10 days of traffic by 31 would drag it
            down artificially).
        </p>
        <p>Source: <code>bi_viz.dashboard_users_activity</code>.</p>`,

    mau: /* HTML */ `<p>
            Monthly Active Users - the number of distinct players active at any
            point during the calendar month.
        </p>
        <p>
            <strong>What's counted:</strong> distinct <code>player_id</code>
            grouped by month (<code>COUNT(DISTINCT player_id)</code>). A player
            active on only one day of the month counts once, the same as a
            player active every day - so MAU ≤ the sum of that month's DAU.
        </p>
        <p>Source: <code>bi_viz.dashboard_users_activity</code>.</p>`,

    monthly_new_users: /* HTML */ `<p>
            Number of players whose first-ever app open falls within the given
            month.
        </p>
        <p>
            <strong>What's counted:</strong> distinct players whose first-open
            date lands in that month (<code>COUNT(DISTINCT d0_player_id)</code>
            grouped by month) - the monthly roll-up of "Daily new users". Each
            player is counted in exactly one month.
        </p>
        <p>Source: <code>bi_viz.dashboard_users_activity</code>.</p>`,
} as const;
